/**
 * ⚠️ DRAFT — REQUIRES LEGAL REVIEW BEFORE LAUNCH ⚠️
 * This privacy policy is a template drafted by AI. It must be reviewed and
 * approved by a qualified legal professional before publication.
 */
import React from "react";
import { Link } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";

const content = {
  en: {
    title: "Privacy Policy",
    lastUpdated: "Last updated: August 2026",
    draft: "⚠️ DRAFT — This document requires legal review before launch.",
    backToHome: "← Back to Home",
    sections: [
      {
        heading: "1. Introduction",
        body: `Mizano ("we", "us", or "our") is a personal finance record-keeping application operated from the State of Kuwait. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our mobile and web application (the "Service"). By using Mizano you agree to the practices described in this policy.`,
      },
      {
        heading: "2. Information We Collect",
        body: `We collect the following categories of personal data:

• Account information: your full name, email address, and phone number (optional).
• Financial data: bank statement PDFs, SMS text messages from banks, and transaction data extracted from those sources (amounts, dates, merchant names, categories, balances).
• Uploaded images: photographs of receipts or bank statements you upload for parsing.
• Usage data: pages visited, features used, device type, and browser information for analytics purposes.
• Authentication tokens: session cookies required to keep you signed in.`,
      },
      {
        heading: "3. How We Use Your Information",
        body: `We use your information exclusively to:

• Parse and categorize your financial transactions automatically.
• Display dashboards, income statements, analytics, and budget tracking.
• Generate exportable reports (PDF, Excel) of your financial data.
• Improve parsing accuracy and fix processing errors.
• Communicate service updates or respond to support requests.`,
      },
      {
        heading: "4. Third-Party Processing & Storage",
        body: `To parse uploaded bank statements, SMS messages, and images, your documents are transmitted to a third-party Large Language Model (LLM) provider for automated text extraction and categorization. The LLM provider processes the content in real-time and does not retain your data after processing is complete.

Your extracted transaction data and uploaded files are stored in cloud object storage and a cloud-hosted database. All data is encrypted in transit (TLS 1.2+) and at rest.`,
      },
      {
        heading: "5. Debugging & Error Review",
        body: `When automated parsing fails, the failed parsing attempt (which may include fragments of the original document or message) may be reviewed by the application operator for the sole purpose of diagnosing and fixing the parsing error. Such review is limited to what is necessary to resolve the issue.`,
      },
      {
        heading: "6. Data Retention & Deletion",
        body: `We retain your personal data for as long as your account is active. You may delete your account and all associated data at any time by contacting us at the address below. Upon receiving a deletion request, we will permanently remove your data within 30 calendar days, except where retention is required by applicable law.`,
      },
      {
        heading: "7. Data Sharing",
        body: `We do not sell, rent, or share your personal information with advertisers or marketing companies. We do not monetize your financial data in any way. Data is shared only with:

• Cloud infrastructure providers (for hosting and storage).
• The LLM provider (for document parsing, as described above).
• Law enforcement, if required by a valid legal order under Kuwaiti law.`,
      },
      {
        heading: "8. Your Rights",
        body: `Under Kuwait's Communication and Information Technology Regulatory Authority (CITRA) Data Privacy Protection Regulation, you have the right to:

• Access the personal data we hold about you.
• Request correction of inaccurate data.
• Request deletion of your data.
• Withdraw consent for data processing.

To exercise any of these rights, contact us using the details in Section 10.`,
      },
      {
        heading: "9. Security",
        body: `We implement industry-standard security measures including encrypted connections, secure authentication, and access controls. However, no system is 100% secure. You are responsible for keeping your login credentials confidential.`,
      },
      {
        heading: "10. Contact Us",
        body: `For data requests, questions, or concerns about this Privacy Policy:

Email: mizanoapphelp@gmail.com
Location: State of Kuwait`,
      },
      {
        heading: "11. Changes to This Policy",
        body: `We may update this Privacy Policy from time to time. Material changes will be communicated through the application. Continued use of the Service after changes constitutes acceptance of the revised policy.`,
      },
    ],
  },
  ar: {
    title: "سياسة الخصوصية",
    lastUpdated: "آخر تحديث: أغسطس 2026",
    draft: "⚠️ مسودة — يتطلب هذا المستند مراجعة قانونية قبل الإطلاق.",
    backToHome: "→ العودة إلى الرئيسية",
    sections: [
      {
        heading: "1. المقدمة",
        body: `ميزانو ("نحن" أو "لنا") هو تطبيق لإدارة السجلات المالية الشخصية يُدار من دولة الكويت. توضح سياسة الخصوصية هذه كيفية جمع معلوماتك الشخصية واستخدامها وتخزينها وحمايتها عند استخدامك لتطبيقنا ("الخدمة"). باستخدامك لميزانو فإنك توافق على الممارسات الموضحة في هذه السياسة.`,
      },
      {
        heading: "2. المعلومات التي نجمعها",
        body: `نجمع الفئات التالية من البيانات الشخصية:

• معلومات الحساب: اسمك الكامل وعنوان بريدك الإلكتروني ورقم هاتفك (اختياري).
• البيانات المالية: كشوف الحسابات البنكية بصيغة PDF، والرسائل النصية القصيرة من البنوك، وبيانات المعاملات المستخرجة منها (المبالغ والتواريخ وأسماء التجار والفئات والأرصدة).
• الصور المرفوعة: صور الإيصالات أو كشوف الحسابات البنكية التي ترفعها للتحليل.
• بيانات الاستخدام: الصفحات التي تمت زيارتها والميزات المستخدمة ونوع الجهاز ومعلومات المتصفح لأغراض التحليل.
• رموز المصادقة: ملفات تعريف الارتباط المطلوبة لإبقائك مسجلاً الدخول.`,
      },
      {
        heading: "3. كيف نستخدم معلوماتك",
        body: `نستخدم معلوماتك حصرياً من أجل:

• تحليل وتصنيف معاملاتك المالية تلقائياً.
• عرض لوحات المعلومات وكشوف الدخل والتحليلات وتتبع الميزانية.
• إنشاء تقارير قابلة للتصدير (PDF، Excel) لبياناتك المالية.
• تحسين دقة التحليل وإصلاح أخطاء المعالجة.
• إرسال تحديثات الخدمة أو الرد على طلبات الدعم.`,
      },
      {
        heading: "4. المعالجة والتخزين لدى أطراف ثالثة",
        body: `لتحليل كشوف الحسابات البنكية والرسائل النصية والصور المرفوعة، يتم إرسال مستنداتك إلى مزود نموذج لغوي كبير (LLM) تابع لطرف ثالث لاستخراج النص وتصنيفه تلقائياً. يعالج مزود النموذج اللغوي المحتوى في الوقت الفعلي ولا يحتفظ ببياناتك بعد اكتمال المعالجة.

يتم تخزين بيانات المعاملات المستخرجة والملفات المرفوعة في تخزين سحابي وقاعدة بيانات مستضافة سحابياً. جميع البيانات مشفرة أثناء النقل (TLS 1.2+) وأثناء التخزين.`,
      },
      {
        heading: "5. التصحيح ومراجعة الأخطاء",
        body: `عند فشل التحليل التلقائي، قد تتم مراجعة محاولة التحليل الفاشلة (التي قد تتضمن أجزاء من المستند أو الرسالة الأصلية) من قبل مشغل التطبيق لغرض وحيد هو تشخيص خطأ التحليل وإصلاحه. تقتصر هذه المراجعة على ما هو ضروري لحل المشكلة.`,
      },
      {
        heading: "6. الاحتفاظ بالبيانات وحذفها",
        body: `نحتفظ ببياناتك الشخصية طالما أن حسابك نشط. يمكنك حذف حسابك وجميع البيانات المرتبطة به في أي وقت عن طريق الاتصال بنا على العنوان أدناه. عند تلقي طلب الحذف، سنقوم بإزالة بياناتك نهائياً خلال 30 يوماً تقويمياً، باستثناء الحالات التي يتطلب فيها القانون المعمول به الاحتفاظ بها.`,
      },
      {
        heading: "7. مشاركة البيانات",
        body: `لا نبيع أو نؤجر أو نشارك معلوماتك الشخصية مع المعلنين أو شركات التسويق. لا نستثمر بياناتك المالية بأي شكل من الأشكال. تتم مشاركة البيانات فقط مع:

• مزودي البنية التحتية السحابية (للاستضافة والتخزين).
• مزود النموذج اللغوي (لتحليل المستندات، كما هو موضح أعلاه).
• الجهات القانونية، إذا كان ذلك مطلوباً بموجب أمر قانوني صالح بموجب القانون الكويتي.`,
      },
      {
        heading: "8. حقوقك",
        body: `بموجب لائحة حماية خصوصية البيانات الصادرة عن هيئة تنظيم الاتصالات وتقنية المعلومات (CITRA) في الكويت، لديك الحق في:

• الوصول إلى البيانات الشخصية التي نحتفظ بها عنك.
• طلب تصحيح البيانات غير الدقيقة.
• طلب حذف بياناتك.
• سحب الموافقة على معالجة البيانات.

لممارسة أي من هذه الحقوق، اتصل بنا باستخدام التفاصيل الواردة في القسم 10.`,
      },
      {
        heading: "9. الأمان",
        body: `ننفذ تدابير أمنية وفقاً لمعايير الصناعة بما في ذلك الاتصالات المشفرة والمصادقة الآمنة وضوابط الوصول. ومع ذلك، لا يوجد نظام آمن بنسبة 100%. أنت مسؤول عن الحفاظ على سرية بيانات تسجيل الدخول الخاصة بك.`,
      },
      {
        heading: "10. اتصل بنا",
        body: `لطلبات البيانات أو الأسئلة أو المخاوف بشأن سياسة الخصوصية هذه:

البريد الإلكتروني: mizanoapphelp@gmail.com
الموقع: دولة الكويت`,
      },
      {
        heading: "11. التغييرات على هذه السياسة",
        body: `قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سيتم إبلاغ التغييرات الجوهرية من خلال التطبيق. يعتبر استمرار استخدام الخدمة بعد التغييرات بمثابة قبول للسياسة المعدلة.`,
      },
    ],
  },
};

export default function Privacy() {
  const { language, dir, isRTL } = useLanguage();
  const c = content[language];

  return (
    <div className="min-h-screen bg-background text-foreground safe-area-top safe-area-bottom" dir={dir}>
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link href="/" className="text-primary hover:text-primary/80 text-sm">
            {c.backToHome}
          </Link>
        </div>

        <div className="bg-card text-card-foreground rounded-2xl p-6 md:p-10 shadow-lg border border-border">
          {/* Draft warning */}
          <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-4 mb-6">
            <p className="text-yellow-300 text-sm font-medium">{c.draft}</p>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{c.title}</h1>
          <p className="text-muted-foreground text-sm mb-8">{c.lastUpdated}</p>

          {/* Sections */}
          <div className="space-y-8">
            {c.sections.map((section, idx) => (
              <div key={idx}>
                <h2 className="text-lg font-semibold text-foreground mb-3">{section.heading}</h2>
                <div className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
                  {section.body}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer links */}
        <div className="mt-8 text-center">
          <Link href="/terms" className="text-primary hover:text-primary/80 text-sm">
            {language === "en" ? "Terms of Service" : "شروط الخدمة"}
          </Link>
        </div>
      </div>
    </div>
  );
}
